import { create } from 'zustand';

import { FEEDBACK_INITIAL_SORT_STATUS } from '@/configs/filter';
import TABLE_KEY from '@/configs/table-key';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import service from '@/services/index';
import useToastStore from '@/stores/pure/ToastStore';
import type { CATEGORY_TYPE } from '@/types/categoryConst';
import type { FeedbackSentiment } from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import LRUCache from '@/utils/cache';
import { getDateRangeByDateT, RangeUnit } from '@/utils/getDatePreset';

export const FEEDBACK_LIST_PAGE_SIZE = 50;

export class FeedbackMapT extends LRUCache<string, PaginatedData<FeedbackT>> {}

type States = {
  feedbackMap: FeedbackMapT;
  updatedFeedbackList: string[];
  feedbackAliasesInPage: string[];
  relatedFeedbackMap: { [key: string]: string[] };
  listLoading: boolean;

  pageAliases: {
    prevAlias: string | null;
    nextAlias: string | null;
    currentAlias: string | null;
  };

  paginationInfo: PaginationInfoT;

  searchValue: string;
  fetchParams: {
    dateRange: [DateString, DateString];
    page: number;
    sortStatus: SortStatusT;
    feedbackListFilter: StructuredFilterQueryFormatT[];
  };
};

type Actions = {
  setState: StoreSetter<States>;
  updateFeedback: (feedback: FeedbackT) => void;
  addNewFeedback: (
    feedback: PostFeedbackParams,
    isCSV?: boolean,
  ) => Promise<FeedbackT>;
  setSendFeedback: (feedback: FeedbackT, isProcessed?: boolean) => void;
  mergeUpdatedFeedback: () => void;
  removeFeedback: (feedbackAlias: string) => Promise<void>;
  removeFeedbacks: (feedbackAliases: string[]) => Promise<void>;
  setRemoveFeedback: (feedbackAliases: string[]) => void;
  toggleTag: (
    feedbackAlias: string,
    tagKey: string,
    action: 'add' | 'remove',
  ) => Promise<any>;
  addTagsToFeedbacks: (feedbackAliases: string[], tagKeys: string[]) => void;
  removeTagsFromFeedbacks: (
    feedbackAliases: string[],
    tagKeys: string[],
  ) => void;
  pushFeedbackDetail: (feedback: FeedbackT) => void;
  fetchFeedbackList: () => Promise<FeedbackListT>;
  fetchFeedbackDetail: (key: string, isPublic?: boolean) => Promise<FeedbackT>;
  addBookmark: (
    feedbackAlias: string,
    bookmark: PostBookmarkParams,
  ) => Promise<BookmarkT>;
  editBookmark: (
    feedbackAlias: string,
    bookmarkAlias: string,
    bookmark: PostBookmarkParams,
  ) => Promise<BookmarkT>;
  removeBookmark: (
    feedbackAlias: string,
    bookmarkAlias: string,
  ) => Promise<BookmarkT[]>;
  setNewBookmark: (feedbackAlias: string, bookmark: BookmarkT) => void;
  setNewPropertyList: (
    feedbackAlias: string,
    propertyKey: string,
    propertyName: 'labels',
  ) => boolean;
  setNewProperty: (
    feedbackAlias: string,
    propertyKey: string | string[] | number | CATEGORY_TYPE[] | null,
    propertyName: keyof FeedbackListItemT,
  ) => void;

  changeSentiment: (feedbackAlias: string, sentimentKey: number) => boolean;
  changeEndingSentiment: (feedbackAlias: string, sentimentKey: number) => void;
  changeTitle: (feedbackAlias: string, title: string) => boolean;
  changePriority: (feedbackAlias: string, priorityKey: PriorityKeyT) => void;
  changeDataOrigin: (feedbackAlias: string, data_origin: string | null) => void;
  changeScore: (
    feedbackAlias: string,
    score: number | null,
    field_name: 'rating_score' | 'nps_score' | 'ces_score' | 'csat_score',
  ) => void;
  changeAgent: (feedbackAlias: string, agentAlias: string | null) => void;
  changeAgentOfFeedbacks: (
    feedbackAliases: string[],
    agentAlias: string | null,
  ) => void;
  updateDate: (feedbackAlias: string, date: string) => void;
  getRelatedFeedbacks: (feedbackAlias: string) => void;
  addTagInFeedback: (feedbackAlias: string, tagAlias: string) => void;
  removeTagInFeedback: (feedbackAlias: string, tagAlias: string) => void;
  changeDataOriginOfFeedbacks: (
    feedbackAliases: string[],
    data_origin: string | null,
  ) => void;
  setPageAliases: (currentFeedbackAlias: string | null) => void;
  setPaginationInfo: (paginationInfo: PaginationInfoT) => void;
  updateFetchParams: (params: Partial<States['fetchParams']>) => void;
  initFeedbackStore: () => void;
};

const initialState: States = {
  feedbackMap: new FeedbackMapT(100),
  updatedFeedbackList: [],
  feedbackAliasesInPage: [],
  relatedFeedbackMap: {},
  paginationInfo: {
    page: 0,
    count: 0,
    size: FEEDBACK_LIST_PAGE_SIZE,
  },
  searchValue: '',
  listLoading: false,
  pageAliases: {
    prevAlias: null,
    nextAlias: null,
    currentAlias: null,
  },

  fetchParams: {
    dateRange: ['', ''] as [DateString, DateString],
    sortStatus: FEEDBACK_INITIAL_SORT_STATUS,
    feedbackListFilter: [],
    page: 0,
  },
};
const useFeedbackListStore = create<States & Actions>((set, get) => {
  // fetchParams를 지연 초기화
  const initializeFetchParams = () => {
    const current = get().fetchParams;
    if (current.dateRange[0] === '' && current.dateRange[1] === '') {
      const dateRange = getDateRangeByDateT(RangeUnit.Daily, 2);
      set({
        fetchParams: {
          ...current,
          dateRange: [dateRange[0].toJSON(), dateRange[1].toJSON()] as [
            DateString,
            DateString,
          ],
        },
      });
    }
  };

  return {
    ...initialState,
    setState: (state) => set(state),
    updateFeedback(feedback) {
      const newMap = get().feedbackMap.clone();
      if (!newMap.get(feedback.alias)) {
        newMap.add(feedback.alias, feedback);
      }
      set(() => ({
        feedbackMap: newMap,
      }));
    },
    setSendFeedback(feedback, isProcessed) {
      const { feedbackMap, pushFeedbackDetail } = get();
      if (feedbackMap.get(feedback.alias)) {
        if (isProcessed) {
          pushFeedbackDetail(feedback);
        }
      } else {
        set((state) => {
          const newArr = [...state.updatedFeedbackList];
          const targetIndex = newArr.findIndex(
            (alias) => alias === feedback.alias,
          );
          if (targetIndex === -1) {
            newArr.push(feedback.alias);
          } else {
            newArr.splice(targetIndex, 1, feedback.alias);
          }
          return {
            ...state,
            updatedFeedbackList: newArr,
          };
        });
      }
    },
    mergeUpdatedFeedback() {
      set({
        updatedFeedbackList: [],
      });
    },
    async addNewFeedback(feedback, isCSV) {
      const result = await service.feedback.postFeedback(feedback, isCSV);
      const { feedbackMap, pushFeedbackDetail, paginationInfo } = get();
      if (paginationInfo.page !== 0) return result;

      if (feedbackMap.get(result.alias)) {
        pushFeedbackDetail(result);
      } else {
        const obj: FeedbackT = {
          ...result,
        };
        set((state) => {
          let prevArr = state.feedbackAliasesInPage.reduce((acc, alias) => {
            const feedback = feedbackMap.get(alias);
            if (feedback) {
              acc.push(feedback);
            }
            return acc;
          }, [] as FeedbackT[]);
          if (prevArr.length >= FEEDBACK_LIST_PAGE_SIZE) {
            prevArr = prevArr.slice(0, -1);
          }
          let prevFeedbackAliasesInPage = state.feedbackAliasesInPage;
          if (prevFeedbackAliasesInPage.length >= FEEDBACK_LIST_PAGE_SIZE) {
            prevFeedbackAliasesInPage = prevFeedbackAliasesInPage.slice(0, -1);
          }
          const newArr = [obj, ...prevArr];
          const newFeedbackAliasesInPage = [
            result.alias,
            ...prevFeedbackAliasesInPage,
          ];
          return {
            feedbackMap: newArr.reduce((acc, value) => {
              acc.add(value.alias, value);
              return acc;
            }, new FeedbackMapT(100)),
            feedbackAliasesInPage: newFeedbackAliasesInPage,
          };
        });
      }
      return result;
    },
    async removeFeedback(feedbackAlias) {
      await service.feedback.removeFeedback(feedbackAlias);
      get().setRemoveFeedback([feedbackAlias]);
    },
    async removeFeedbacks(feedbackAliases) {
      await service.feedback.removeFeedbacks(feedbackAliases);
      get().setRemoveFeedback(feedbackAliases);
    },
    setRemoveFeedback(feedbackAliases) {
      const newMap = get().feedbackMap.clone();
      for (const feedbackAlias of feedbackAliases) {
        const feedback = newMap.get(feedbackAlias);
        if (!feedback) return;
        newMap.delete(feedbackAlias);
      }

      const newFeedbackAliasesInPage = get().feedbackAliasesInPage.filter(
        (alias) => !feedbackAliases.includes(alias),
      );

      set(() => ({
        feedbackMap: newMap,
        feedbackAliasesInPage: newFeedbackAliasesInPage,
      }));
    },
    async addTagsToFeedbacks(feedbackAliases, tagKeys) {
      const feedbackMap = get().feedbackMap.clone();
      const promises: Promise<any>[] = [];
      const updatedItems: string[][] = [];
      tagKeys.map((tagKey) => {
        feedbackAliases.map((fa) => {
          // If the tag is not included in current feedback, add it
          if (!feedbackMap.get(fa)?.labels?.includes(tagKey)) {
            updatedItems.push([fa, tagKey]);
            promises.push(get().toggleTag(fa, tagKey, 'add'));
          }
        });
      });

      await Promise.all(promises);
    },
    async removeTagsFromFeedbacks(feedbackAliases, tagKeys) {
      const feedbackMap = get().feedbackMap.clone();
      const promises: Promise<any>[] = [];
      const updatedItems: string[][] = [];
      tagKeys.map((tagKey) => {
        feedbackAliases.map((fa) => {
          // If the tag is included in current feedback, remove it
          if (feedbackMap.get(fa)?.labels?.includes(tagKey)) {
            updatedItems.push([fa, tagKey]);
            promises.push(get().toggleTag(fa, tagKey, 'remove'));
          }
        });
      });

      await Promise.all(promises);
    },
    async toggleTag(feedbackAlias, tagKey, action) {
      const result = get().setNewPropertyList(feedbackAlias, tagKey, 'labels');
      if (action === 'add') {
        await service.feedback.addTag(feedbackAlias, tagKey);
      } else {
        await service.feedback.removeTag(feedbackAlias, tagKey);
      }

      return result;
    },
    setNewPropertyList(feedbackAlias, propertyKey, propertyName) {
      const newMap = get().feedbackMap.clone();
      const feedback = newMap.get(feedbackAlias);
      if (!feedback) return false;
      const newPropertyList = feedback[propertyName] || [];

      if (newPropertyList.includes(propertyKey)) {
        const idx = newPropertyList.indexOf(propertyKey);
        if (idx > -1) newPropertyList.splice(idx, 1);

        newMap.add(feedbackAlias, {
          ...feedback,
          [propertyName]: newPropertyList,
        });

        set(() => ({
          feedbackMap: newMap,
        }));

        return false;
      } else {
        newPropertyList.push(propertyKey);

        newMap.add(feedbackAlias, {
          ...feedback,
          [propertyName]: newPropertyList,
        });

        set(() => ({
          feedbackMap: newMap,
        }));

        return true;
      }
    },
    async setNewProperty(feedbackAlias, propertyKey, propertyName) {
      const newMap = get().feedbackMap.clone();

      if (!newMap.get(feedbackAlias)) return;

      const result = await getNewFeedbackMapWithNewProperty(
        newMap,
        feedbackAlias,
        propertyKey,
        propertyName,
      );

      set({
        feedbackMap: result,
      });
    },

    changeSentiment(feedbackAlias, sentimentKey) {
      get().setNewProperty(feedbackAlias, sentimentKey, 'sentiment');
      service.feedback.changeSentiment(feedbackAlias, sentimentKey);
      return true;
    },
    changeEndingSentiment(feedbackAlias, sentimentKey) {
      get().setNewProperty(feedbackAlias, sentimentKey, 'ending_sentiment');
      service.feedback.changeEndingSentiment(feedbackAlias, sentimentKey);
    },
    changeScore(feedbackAlias, score, field_name) {
      get().setNewProperty(feedbackAlias, score, field_name);
      service.feedback.updateScore(feedbackAlias, score, field_name);
    },
    changeTitle(feedbackAlias, title) {
      get().setNewProperty(feedbackAlias, title, 'title');
      service.feedback.changeTitle(feedbackAlias, title);
      return true;
    },
    changePriority(feedbackAlias, priorityKey) {
      get().setNewProperty(
        feedbackAlias,
        priorityKey,
        'priority',
      );
      service.feedback.changePriority(feedbackAlias, priorityKey);
      return true;
    },
    changeDataOrigin(feedbackAlias, data_origin) {
      if (!data_origin) {
        service.feedback.resetDataOrigin(feedbackAlias);

        const newMap = get().feedbackMap.clone();
        const feedback = newMap.get(feedbackAlias);
        if (!feedback) throw new Error('Feedback not exist');
        newMap.add(feedbackAlias, {
          ...feedback,
          data_origin: undefined,
        });
        set((state) => ({
          ...state,
          feedbackMap: newMap,
        }));
        return;
      }
      service.feedback.changeDataOrigin(feedbackAlias, data_origin);

      get().setNewProperty(feedbackAlias, data_origin, 'data_origin');
    },
    changeAgent(feedbackAlias, agentAlias) {
      if (!agentAlias) {
        service.feedback.resetAgent(feedbackAlias);
      } else {
        service.feedback.changeAgent(feedbackAlias, agentAlias);
      }

      get().setNewProperty(feedbackAlias, agentAlias, 'manager');
    },
    async changeAgentOfFeedbacks(feedbackAliases, agentAlias) {
      const feedbackMap = get().feedbackMap.clone();
      const promises: Promise<any>[] = [];
      const updatedFeedbackAliases: string[] = [];

      feedbackAliases.map((fa) => {
        const feedback = feedbackMap.get(fa);
        if (!feedback) return;
        if (agentAlias === null && feedback.manager !== undefined) {
          // If unassigning agent and any agent is applied to current feedback, reset it
          updatedFeedbackAliases.push(fa);
          promises.push(service.feedback.resetAgent(fa));
        } else if (agentAlias !== null && feedback.manager !== agentAlias) {
          // If assigning an agent and the agent is not applied to current feedback, apply it
          updatedFeedbackAliases.push(fa);
          promises.push(service.feedback.changeAgent(fa, agentAlias));
        }
      });

      await Promise.all(promises);
      updatedFeedbackAliases.map((fa) =>
        get().setNewProperty(fa, agentAlias, 'manager'),
      );
    },
    updateDate(feedbackAlias, date) {
      service.feedback.updateDate(feedbackAlias, date);
      get().setNewProperty(feedbackAlias, date, 'date');
    },
    async addBookmark(feedbackAlias, bookmark) {
      const data = await service.feedback.postBookmarkToFeedback(
        bookmark,
        feedbackAlias,
      );
      get().setNewBookmark(feedbackAlias, data);
      return data;
    },
    setNewBookmark(feedbackAlias, bookmark) {
      const newMap = get().feedbackMap.clone();
      const feedback = newMap.get(feedbackAlias);
      if (!feedback) throw new Error('Feedback not exist');
      newMap.add(feedbackAlias, {
        ...feedback,
        bookmarks: (feedback.bookmarks || []).concat([bookmark]),
      });

      set((state) => ({
        ...state,
        feedbackMap: newMap,
      }));
    },
    async removeBookmark(feedbackAlias, bookmarkId) {
      const newMap = get().feedbackMap.clone();
      const feedback = newMap.get(feedbackAlias);
      if (!feedback) throw new Error('Feedback not exist');
      let newBookmarkList = feedback.bookmarks || [];

      newBookmarkList = newBookmarkList.filter((b) => b.alias != bookmarkId);
      newMap.add(feedbackAlias, {
        ...feedback,
        bookmarks: newBookmarkList,
      });

      set((state) => ({
        ...state,
        feedbackMap: newMap,
      }));

      await service.feedback.deleteBookmarkToFeedback(
        bookmarkId,
        feedbackAlias,
      );

      return newBookmarkList;
    },
    async editBookmark(feedbackAlias, bookmarkId, bookmark) {
      const newMap = get().feedbackMap.clone();
      const feedback = newMap.get(feedbackAlias);
      if (!feedback) throw new Error('Feedback not exist');
      let newBookmarkList = feedback.bookmarks || [];

      newBookmarkList = newBookmarkList.map((b) => {
        if (b.alias === bookmarkId) {
          return {
            alias: bookmarkId,
            ...bookmark,
          };
        }
        return b;
      });

      newMap.add(feedbackAlias, {
        ...feedback,
        bookmarks: newBookmarkList,
      });

      set((state) => ({
        ...state,
        feedbackMap: newMap,
      }));

      await service.feedback.editBookmarkToFeedback(
        bookmark,
        bookmarkId,
        feedbackAlias,
      );

      return {
        alias: bookmarkId,
        ...bookmark,
      };
    },
    pushFeedbackDetail(feedback) {
      const newUpdatedList = [...get().updatedFeedbackList];
      const newMap = get().feedbackMap.clone();

      const feedbackListItem = newMap.get(feedback.alias) || {};
      const updatedFeedback = {
        ...feedbackListItem,
        ...feedback,
      };
      newMap.add(feedback.alias, updatedFeedback);
      set({
        updatedFeedbackList: newUpdatedList.filter(
          (alias) => alias !== feedback.alias,
        ),
        feedbackMap: newMap,
      });
    },
    async fetchFeedbackList() {
      initializeFetchParams();

      const {
        page,
        sortStatus,
        feedbackListFilter: filterQueryFormats,
        dateRange,
      } = get().fetchParams;

      const {
        paginationInfo,
        searchValue,
        feedbackMap,
        feedbackAliasesInPage,
      } = get();

      set({ listLoading: true });

      const structuredFilter =
        structuredFilterUtils.buildStructuredFilter(filterQueryFormats) || {};

      structuredFilter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] =
        structuredFilterUtils.buildSingleValue(searchValue);

      const getDirection = () => {
        if (!page) return 'next';
        if (paginationInfo.page > page) return 'prev';
        return 'next';
      };

      const direction = getDirection();

      const getCursor = () => {
        if (!page) return undefined;
        if (feedbackAliasesInPage.length > 0) {
          return feedbackMap.get(
            feedbackAliasesInPage[
              direction === 'next' ? feedbackAliasesInPage.length - 1 : 0
            ],
          )?.paginate_token;
        }
        return undefined;
      };

      const cursor = getCursor();

      const result = await service.feedback.fetchFeedbackList({
        query: {
          sortby: sortStatus?.sortBy || undefined,
          order: sortStatus?.orderStatus || undefined,
          cursor,
          direction,
        },
        payload: {
          ...structuredFilter,
          ...structuredFilterUtils.buildDateFilter({
            start_date: dateRange[0],
            end_date: dateRange[1],
          }),
        },
      });

      const feedbacksArr = result.feedbacks;
      const feedbacks = get().feedbackMap.clone();
      const newFeedbackAliasesInPage: string[] = [];
      const mapArray: [string, FeedbackListItemT][] = [];

      feedbacksArr?.forEach((feedback) => {
        newFeedbackAliasesInPage.push(feedback.alias);
        mapArray.push([feedback.alias, feedback]);
      });

      feedbacks.set(mapArray);

      const pos = direction === 'next' ? 0 : 2000;

      const target = document?.getElementById(TABLE_KEY.FEEDBACK_TABLE_KEY);
      if (target) {
        target.scrollTop = pos;
      }

      set((prev) => {
        return {
          feedbackMap: feedbacks,
          feedbackAliasesInPage: newFeedbackAliasesInPage,
          paginationInfo: {
            ...prev.paginationInfo,
            count: result.count,
            page: page,
          },
          listLoading: false,
        };
      });

      return result;
    },
    async fetchFeedbackDetail(key, isPublic = false) {
      const result = await service.feedback.fetchFeedbackDetail(key, isPublic);

      const newFeedbackMap = get().feedbackMap.clone();
      const feedback = newFeedbackMap.get(key);

      newFeedbackMap.add(key, {
        ...(feedback || {}),
        ...result,
      });

      return result;
    },
    async getRelatedFeedbacks(feedbackAlias) {
      try {
        const res = await service.feedback.fetchRelateFeedbacks(feedbackAlias);
        set((state) => ({
          relatedFeedbackMap: {
            ...state.relatedFeedbackMap,
            [feedbackAlias]: res,
          },
        }));
      } catch (e) {
        console.error(e);
        throw e;
      }
    },

    addTagInFeedback: (feedbackAlias, tagAlias) => {
      const { feedbackMap, updateFeedback } = get();
      const feedback = feedbackMap.get(feedbackAlias);
      if (!feedback) return;
      const newLabels = feedback.labels
        ? [...new Set([...feedback.labels, tagAlias])]
        : [tagAlias];

      updateFeedback({
        ...feedback,
        labels: newLabels,
      });
    },
    removeTagInFeedback: (feedbackAlias, tagAlias) => {
      const { feedbackMap, updateFeedback } = get();
      const feedback = feedbackMap.get(feedbackAlias);
      if (!feedback) return;
      const label = feedback.labels ? feedback.labels : [];

      updateFeedback({
        ...feedback,
        labels: label.filter((item) => item !== tagAlias),
      });
    },

    async changeDataOriginOfFeedbacks(feedbackAliases, data_origin) {
      const backup = get().feedbackMap.clone();
      let result = get().feedbackMap.clone();
      try {
        await service.feedback.updateMultipleFeedbackField(
          feedbackAliases,
          'data_origin',
          data_origin,
        );

        for (let i = 0; i < feedbackAliases.length; i++) {
          const feedbackAlias = feedbackAliases[i];
          if (!result.get(feedbackAlias)) return;
          result = await getNewFeedbackMapWithNewProperty(
            result,
            feedbackAlias,
            data_origin,
            'data_origin',
          );
        }
        useToastStore.getState().addDefaultToast('Feedback updated', 'success');
        set({
          feedbackMap: result,
        });
      } catch (e) {
        useToastStore.getState().addDefaultToast('Update failed', 'fail');
        set({
          feedbackMap: backup,
        });
      }
    },

    setPageAliases: (currentFeedbackAlias) => {
      const { feedbackAliasesInPage } = get();

      if (!currentFeedbackAlias) {
        set({
          pageAliases: {
            prevAlias: null,
            nextAlias: null,
            currentAlias: null,
          },
        });
        return;
      }
      const currIndex = feedbackAliasesInPage.findIndex(
        (key) => key === currentFeedbackAlias,
      );
      if (currIndex !== -1) {
        if (currIndex === feedbackAliasesInPage.length - 1) {
          set({
            pageAliases: {
              prevAlias: feedbackAliasesInPage[currIndex - 1] || null,
              nextAlias: null,
              currentAlias: currentFeedbackAlias,
            },
          });
          return;
        }
        if (currIndex === 0) {
          set({
            pageAliases: {
              prevAlias: null,
              nextAlias: feedbackAliasesInPage[1] || null,
              currentAlias: currentFeedbackAlias,
            },
          });
          return;
        }
        set({
          pageAliases: {
            prevAlias: feedbackAliasesInPage[currIndex - 1] || null,
            nextAlias: feedbackAliasesInPage[currIndex + 1] || null,
            currentAlias: currentFeedbackAlias,
          },
        });
      }
    },

    setPaginationInfo(paginationInfo) {
      set({
        paginationInfo,
      });
    },

    updateFetchParams(params) {
      set((prev) => {
        return {
          fetchParams: { ...prev.fetchParams, ...params },
        };
      });
    },
    initFeedbackStore() {
      set(initialState);
    },
  };
});

export const getNewFeedbackMapWithNewProperty = async (
  feedbackMap: FeedbackMapT,
  feedbackAlias: string,
  propertyKey: string | string[] | number | CATEGORY_TYPE[] | null,
  propertyName: keyof FeedbackListItemT,
): Promise<FeedbackMapT> => {
  const newMap = feedbackMap.clone();
  const feedback = feedbackMap.get(feedbackAlias);
  if (!feedback) throw new Error('Feedback not exist');

  if (propertyName === 'sentiment') {
    newMap.add(feedbackAlias, {
      ...feedback,
      sentiment: Number(propertyKey) as FeedbackSentiment,
    });
  } else if (propertyName === 'priority') {
    newMap.add(feedbackAlias, {
      ...feedback,
      priority: Number(propertyKey) as PriorityKeyT,
    });
  } else if (propertyName === 'company_info') {
    if (propertyKey) {
      const company = await service.account.fetchCompany(propertyKey as string);
      newMap.add(feedbackAlias, {
        ...feedback,
        company_info: {
          name: company.name,
        },
      });
    } else {
      newMap.add(feedbackAlias, {
        ...feedback,
        company_info: undefined,
        customer_info: undefined,
      });
    }
  } else if (propertyName === 'classification') {
    newMap.add(feedbackAlias, {
      ...feedback,
      classification: propertyKey as CATEGORY_TYPE[],
    });
  } else if (propertyName === 'manager') {
    newMap.add(feedbackAlias, {
      ...feedback,
      manager: propertyKey ? String(propertyKey) : undefined,
    });
  } else {
    newMap.add(feedbackAlias, {
      ...feedback,
      [propertyName]: String(propertyKey),
    });
  }
  return newMap;
};

export default useFeedbackListStore;
